/**
 * THE SIGN-IN START NEVER WAITS IN SILENCE (2026-10-03, Justin's stuck "getting your threads back…").
 *
 * Firebase checks its own browser database when it boots. A delete of that database that another prest0 tab started
 * and cannot finish (the tab's own SDK holds it open; an older build's sign-out did that) queues every later open of
 * it, so this page's sign-in start waits until every prest0 tab is closed, and every surface waiting on "who is
 * here?" waits with it. The sign-out wipe no longer deletes these databases (siteDataWipe.ts), but a tab still on an
 * older build can, and nothing on this page can release it.
 *
 * So, from the moment the app boots: if sign-in has not answered after WATCH_AFTER_MS, the watch tries to open
 * Firebase's databases, each bounded (./authStallLook.ts). What it finds is stamped once for the beacon
 * (authStallVital.ts). When a database is held, a notice says which step clears it: close the other prest0 tab or
 * window. It goes away by itself the moment sign-in answers, and offers a reload.
 *
 * Browser build only (started from src/utils/firebase.ts after initializeAuth).
 *
 * ONLY A DATABASE THIS BOOT OPENS CAN HOLD IT (2026-10-31). Someone already in local storage boots without Firebase's
 * browser database, and the heartbeat's database is bounded (utils/firebase.ts), so the boot names the databases it
 * still opens (`databasesInUse`) and the watch looks only at those: a slow one it does not open is not the cause, and
 * the notice would send the person after the wrong tab. The watch also stamps when sign-in started and whether its
 * answer found someone, so the beacon reports how long every sign-in took (AUTH_READY, authStallVital.ts).
 *
 * AND THE LOOK IS FETCHED ONLY WHEN IT IS NEEDED (2026-10-31). This module rides the first download of every page, so
 * it keeps only the clock and the stamps. The probe, the notice and its words are their own download
 * (./authStallLook.ts), fetched when sign-in is late and the boot opened a database to look at: a boot that opened
 * none is stamped `unheld` without fetching anything. A look that cannot be fetched claims nothing.
 *
 * A TIMER THAT FIRES EARLY IS NOT A WAIT (2026-11-02). In the week before 2026-11-01, 35 of the 52 stall rows read
 * under 10 s, and the first day of AUTH_READY showed why: on those pages sign-in had answered in about 2 s, yet the
 * watch's 10 s timer had already fired. A browser never fires a timer early by its own clock; automated renderers
 * (crawlers, link previews) that fast-forward timers do, and those pages carried their signature (a zero time to first
 * byte, large layout shifts). So when the timer fires, the watch reads the page's clock, the one a stall's value is
 * measured with, and a timer that came more than EARLY_TIMER_SLACK_MS before its time records nothing, fetches
 * nothing and shows nothing. The answer, when it comes, still records how long sign-in took and finishes the watch.
 */
import { markingPerformance, stampAuthAnswered, stampAuthStall, stampAuthStarted, type AuthStallRung } from './authStallVital';
import { AUTH_DB_NAME, HEARTBEAT_DB_NAME } from './firebaseDatabaseNames';

/** How long sign-in may take to answer before the watch looks. */
export const WATCH_AFTER_MS = 10_000;

/**
 * How far before its time, by the page's clock, the timer may fire and still count. A coarse clock (a browser that
 * rounds it for privacy) can read a little short; a timer that fast-forwarded reads seconds short.
 */
export const EARLY_TIMER_SLACK_MS = 1_000;

/** The page's clock in ms (User Timing's, which the marks use), or NaN where there is none. */
function pageClock(): number {
  try {
    const p = markingPerformance();
    return p && typeof p.now === 'function' ? p.now() : NaN;
  } catch {
    return NaN;
  }
}

/** Firebase's own databases, in the order a held one is reported. */
export const WATCHED_DATABASES: ReadonlyArray<{ name: string; rung: AuthStallRung }> = [
  { name: AUTH_DB_NAME, rung: 'auth-db' },
  { name: HEARTBEAT_DB_NAME, rung: 'heartbeat-db' },
];

type AuthStallLook = typeof import('./authStallLook');

/** The look's own download: the only way this module reaches it. */
function fetchLook(): Promise<AuthStallLook> {
  return import(/* webpackChunkName: "auth-stall-look" */ './authStallLook');
}

export interface AuthStartupWatchDeps {
  /** Subscribe to sign-in's first answer (whether it found someone); returns an unsubscribe. */
  onAnswer: (answered: (signedIn?: boolean) => void) => () => void;
  /** The watched databases this boot opens before sign-in answers; every watched one when not said. */
  databasesInUse?: ReadonlyArray<string>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  /** The look's probe, for tests; the look's own bounded open when not said. */
  opensInTime?: (name: string) => Promise<boolean>;
  /** How the look is fetched, for tests; its own download when not said. */
  loadLook?: () => Promise<AuthStallLook>;
  /** The page's clock in ms, for tests; User Timing's when not said. */
  now?: () => number;
  documentRef?: Document;
  language?: string;
  reload?: () => void;
}

export interface AuthStartupWatch {
  /** What the watch found, once it looked (null before, or when sign-in answered first). */
  readonly found: AuthStallRung | null;
  /** Resolves once the watch has nothing more to do (answered, or looked and finished). */
  readonly done: Promise<void>;
  stop(): void;
}

export function startAuthStartupWatch(deps: AuthStartupWatchDeps): AuthStartupWatch {
  const doc = deps.documentRef ?? (typeof document !== 'undefined' ? document : undefined);
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => {
    const handle = setTimeout(fn, ms) as unknown as { unref?: () => void };
    try { handle.unref?.(); } catch { /* a browser's timer is a number */ }
    return handle;
  });
  const reload = deps.reload ?? (() => { try { window.location.reload(); } catch { /* nothing to do */ } });
  const loadLook = deps.loadLook ?? fetchLook;
  const now = deps.now ?? pageClock;

  let answered = false;
  let stopped = false;
  let found: AuthStallRung | null = null;
  let notice: HTMLElement | null = null;
  let finish: () => void = () => undefined;
  const done = new Promise<void>((resolve) => { finish = resolve; });

  const removeNotice = () => {
    try { notice?.remove(); } catch { /* already gone */ }
    notice = null;
  };

  const watched = deps.databasesInUse
    ? WATCHED_DATABASES.filter((db) => deps.databasesInUse!.includes(db.name))
    : WATCHED_DATABASES;
  stampAuthStarted();
  const startedAt = now();
  // By the page's clock, did the timer come before its time? Never in a browser; always in a renderer that
  // fast-forwards timers. A page without a clock is taken at its timer's word.
  const firedEarly = () => {
    const elapsed = now() - startedAt;
    return Number.isFinite(elapsed) && elapsed < WATCH_AFTER_MS - EARLY_TIMER_SLACK_MS;
  };

  let unsubscribe: () => void = () => undefined;
  const onAnswered = (signedIn?: boolean) => {
    if (answered) return;
    answered = true;
    stampAuthAnswered(signedIn);
    removeNotice();
    try { unsubscribe(); } catch { /* already gone */ }
    finish();
  };
  try {
    unsubscribe = deps.onAnswer(onAnswered);
  } catch {
    finish();
  }

  // What the look found, stamped once for the beacon. Its notice stays only while sign-in is still unanswered
  // (onAnswered removes it and finishes).
  const settle = (rung: AuthStallRung, shown: HTMLElement | null) => {
    found = rung;
    stampAuthStall(rung);
    notice = shown;
    if (answered || stopped) removeNotice();
    if (answered || stopped || rung === 'unheld') finish();
  };

  setTimer(() => {
    if (answered || stopped) { finish(); return; }
    // Not a wait: nothing is stamped, fetched or shown. The answer still records its time and finishes the watch.
    if (firedEarly()) return;
    // No database this boot opens can hold it: nothing to look at, and nothing to fetch.
    if (watched.length === 0) { settle('unheld', null); return; }
    Promise.resolve()
      .then(loadLook)
      .then(async (look) => {
        const rung = await look.findHeldDatabase(watched, deps.opensInTime);
        const shown = rung !== 'unheld' && !answered && !stopped && doc ? look.showStallNotice(doc, deps.language, reload) : null;
        settle(rung, shown);
      })
      .catch(() => finish());
  }, WATCH_AFTER_MS);

  return {
    get found() { return found; },
    done,
    stop() {
      stopped = true;
      removeNotice();
      try { unsubscribe(); } catch { /* already gone */ }
      finish();
    },
  };
}
